// Port of Flutter's splashScreen.dart — the boot gate.
//
// Same order of operations, and the same reasoning: startup is 100% LOCAL so the
// app opens fast. It reads the cached session, runs the block gate against that
// cached row, and only then navigates. Anything that needs the network happens
// AFTER we are already inside the app (AppContext refreshes the user record in
// the background), so a slow server never delays the first screen.
//
//   cached session? ── no ──> /login
//        │ yes
//   blocked (InactiveUser / DeviceName mismatch)? ── yes ──> /blocked
//        │ no
//        └──> /home

import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../../core/state/AppContext';
import { acceptableDeviceIds } from '../../../core/utils/deviceId';
import { userGate } from '../../../core/auth/userGate';
import { SPLASH_IMAGE } from '../../../core/devices/deviceAssets';

export default function SplashScreen() {
  const navigate = useNavigate();
  const { user } = useApp();
  const bootedRef = useRef(false);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    (async () => {
      if (!user) { navigate('/login', { replace: true }); return; }
      // Every form this phone may be bound under — the sibling apps write
      // different ones (model vs Build.ID) for the same device.
      const deviceIds = await acceptableDeviceIds();
      const gate = userGate(user, deviceIds);
      if (gate) {
        navigate('/blocked', { replace: true, state: gate });
        return;
      }
      navigate('/home', { replace: true });
    })();
  }, [user, navigate]);

  return (
    <Box
      sx={{
        height: '100%',
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component="img"
        src={SPLASH_IMAGE}
        alt="Smart Life India"
        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </Box>
  );
}
